import { classNames } from "@/src/utils/cn";
import { XIcon } from "lucide-react";
import { useRef, useState } from "react";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function EmailPillsInput(props: {
  emails: string[];
  onChange: (emails: string[]) => void;
  placeholder?: string;
  className?: string;
}) {
  const [inputValue, setInputValue] = useState("");
  const [error, setError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const addEmails = (raw: string): boolean => {
    const candidates = raw
      .split(/[\s,;]+/)
      .map((c) => c.trim())
      .filter((c) => c !== "");

    if (candidates.length === 0) {
      return false;
    }

    const valid: string[] = [];
    const invalid: string[] = [];
    for (const candidate of candidates) {
      if (!EMAIL_REGEX.test(candidate)) {
        invalid.push(candidate);
      } else if (!props.emails.includes(candidate) && !valid.includes(candidate)) {
        valid.push(candidate);
      }
    }

    if (valid.length > 0) {
      props.onChange([...props.emails, ...valid]);
    }

    if (invalid.length > 0) {
      setError("Please enter a valid email address");
      setInputValue(invalid.join(" "));
      return false;
    }

    setError("");
    setInputValue("");
    return true;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === "," || e.key === " ") {
      e.preventDefault();
      addEmails(inputValue);
      return;
    }

    if (e.key === "Backspace" && inputValue === "" && props.emails.length > 0) {
      props.onChange(props.emails.slice(0, -1));
    }
  };

  const removeEmail = (indexToRemove: number) => {
    props.onChange(props.emails.filter((_, index) => index !== indexToRemove));
  };

  return (
    <div className={props.className}>
      <div
        className="py-2 px-0.5 shadow-sm rounded-md ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-gray-600 cursor-text"
        onClick={() => inputRef.current?.focus()}
      >
        <div className="flex flex-wrap">
          {props.emails.map((email, index) => (
            <span
              key={`${email}-${index}`}
              className="inline-flex mx-1 my-1 items-center px-2.5 py-1 rounded-full text-sm bg-persian-100 text-persian-900"
            >
              {email}
              <button
                type="button"
                className="ml-1 inline-flex bg-persian-200 p-0.5 rounded-full text-persian-400 hover:text-persian-600"
                onClick={(e) => {
                  e.stopPropagation();
                  removeEmail(index);
                }}
              >
                <XIcon className="w-3 h-3" />
              </button>
            </span>
          ))}
          <input
            ref={inputRef}
            type="text"
            className="placeholder:text-gray-400 py-0.5 px-1 text-sm outline-none focus:outline-none focus:ring-0 border-none flex-1 min-w-[200px]"
            placeholder={
              props.emails.length === 0
                ? (props.placeholder ?? "john@acme.com, jane@acme.com...")
                : "Add more..."
            }
            value={inputValue}
            onChange={(e) => {
              setError("");
              setInputValue(e.target.value);
            }}
            onKeyDown={handleKeyDown}
            onPaste={(e) => {
              const text = e.clipboardData.getData("text");
              if (/[\s,;]/.test(text)) {
                e.preventDefault();
                addEmails(text);
              }
            }}
            onBlur={() => {
              if (inputValue.trim() !== "") {
                addEmails(inputValue);
              }
            }}
          />
        </div>
      </div>
      {error !== "" ? (
        <p className={classNames("mt-1 text-sm text-red-600")}>{error}</p>
      ) : null}
    </div>
  );
}
